import { Direction } from '@angular/cdk/bidi';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { RoutePlanningService } from '../../services/route-planning/route-planning.service';
import { OverlayComponent } from 'src/app/@shared/components/design/overlay/overlay.component';
import { Overlay } from '@angular/cdk/overlay';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { CookiesService } from 'src/app/@shared/services/cookies/cookies.service';
import { AddressAutoCompleteMapComponent } from '../../components/route-planner-form/forms/source-destination-form/address-auto-complete/address-auto-complete-map/address-auto-complete-map.component';
import { SEARCH_ADDRESS, SEARCH_ORIGIN } from '../../constants/multi-language.constants';
import { eTab } from '../../enums/route-planning/tabs/tabs.enums';
import { PlannerSurveyOrchestratorService } from '../../services/survey/planner-survey-orchestrator.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-route-planning-container',
  templateUrl: './route-planning-container.component.html',
  styleUrls: ['./route-planning-container.component.scss'],
  standalone: false
})
export class RoutePlanningContainerComponent
  extends OverlayComponent
  implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer: ElementRef;

  direction: Direction = 'rtl';
  mapRefId: string;

  /** Config for the init address-picker map; used to re-open it when results overlay closes. */
  private initMapOverlayConfig: {
    mapComponent: typeof AddressAutoCompleteMapComponent;
    data: any;
  } | null = null;

  /**
   * Watches the map host element for size changes (viewport resize, mobile
   * orientation flip, dev-tools open) and re-syncs the attached CDK overlay's
   * width/height. Audit: "verify proper responsiveness for the map" — without
   * this the overlay was sized once at creation via `getBoundingClientRect()`
   * and stayed stale on every subsequent resize, leaving blank gutters around
   * the map. Detached in ngOnDestroy.
   */
  private mapResizeObserver: ResizeObserver | null = null;

  constructor(
    protected override readonly resizeService: ResizeService,
    protected override readonly overlay: Overlay,

    private readonly routePlanningService: RoutePlanningService,
    private readonly languageService: LanguageService,
    private readonly userLocationService: UserLocationService,
    private readonly cookiesService: CookiesService,
    private readonly plannerSurveyOrchestrator: PlannerSurveyOrchestratorService,
    private readonly modalService: ModalService,
    private readonly localStorageService: LocalStorageService
  ) {
    super(overlay, resizeService);
  }

  override ngOnDestroy(): void {
    this.plannerSurveyOrchestrator.cancelScheduledSurvey();
    if (this.mapResizeObserver) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    super.ngOnDestroy();
  }

  onSurveyViewClosed(): void {
    this.plannerSurveyOrchestrator.onSurveyUiClosed();
  }

  get displaySurveyId$(): Observable<number | null> {
    return this.plannerSurveyOrchestrator.displaySurveyId$;
  }

  ngAfterViewInit(): void {
    this.initializeMultiLanguageTexts();

    // Open address-auto-complete-map on init instead of default map (selection syncs to origin via query params)
    if (this.isDesktop) {
      const title = SEARCH_ADDRESS[this.languageService.getSelectedLanguage().language];
      const initData = {
        title,
        keepMapOpenOnSelect: true,
        closeCallbackFn: (value: any, targetArg?: 'origin' | 'destination' | 'stop') => {
          this.routePlanningService.closeOverlay();

          const address = (value && typeof value === 'object' && 'address' in value)
            ? value.address
            : value;
          const target = targetArg ?? value?.target ?? 'origin';
          if (!address) return;
          const currentTab = this.routePlanningService.getCurrentTab();
          const stayOnTab = currentTab === eTab.SOURCE_DESTINATION || currentTab === eTab.CAR_INTERGRATED;

          if (target === 'origin') {
            this.routePlanningService.setOriginInQueryParams(address);
            if (!stayOnTab) {
              this.routePlanningService.clearOtherAddressParams('origin');
              this.routePlanningService.setTabToSourceDestination();
            }
          } else if (target === 'destination') {
            this.routePlanningService.setDestinationInQueryParams(address);
            if (!stayOnTab) {
              this.routePlanningService.clearOtherAddressParams('destination');
              this.routePlanningService.setTabToSourceDestination();
            }
          } else if (target === 'stop') {
            if (currentTab === eTab.SOURCE_DESTINATION) {
              this.routePlanningService.setStopInQueryParams(address);
              this.routePlanningService.clearOtherAddressParams('stop');
            } else if (currentTab === eTab.CAR_INTERGRATED) {
              this.routePlanningService.setLegInQueryParams(address);
              this.routePlanningService.clearOtherAddressParams('leg');
            } else {
              this.routePlanningService.setStopInQueryParams(address);
              this.routePlanningService.clearOtherAddressParams('stop');
              this.routePlanningService.setTabToSourceDestination();
            }
          }
        },
      };
      this.initMapOverlayConfig = {
        mapComponent: AddressAutoCompleteMapComponent,
        data: initData,
      };
      setTimeout(() => {
        if (this.mapContainer) {
          this.routePlanningService._mapOverlaySub.next({
            mapComponent: AddressAutoCompleteMapComponent,
            data: initData,
          });
        }
      });
    }

    // Check for location permissions silently without showing any popups
    this.userLocationService.checkPermissionsSilently().subscribe(() => { });

    this.cookiesService.consentGranted$.subscribe(() => { });

    this.routePlanningService._mapOverlaySub.subscribe((mapOverlayObj) => {
      if (!!mapOverlayObj && this.mapContainer) {
        // If a map overlay is already open (e.g. init address picker), replace it so results map can show
        if (!!this.mapRefId) {
          this.detachOverlayImmediate(this.mapRefId, undefined, false);
          this.mapRefId = null;
        }
        this.mapRefId = this.openComponentOverlay(
          this.direction,
          mapOverlayObj.mapComponent,
          mapOverlayObj.data,
          this.mapContainer,
          mapOverlayObj.data?.closeCallbackFn,
          [
            {
              originX: this.direction === 'rtl' ? 'end' : 'start',
              originY: 'top',
              overlayX: this.direction === 'rtl' ? 'end' : 'start',
              overlayY: 'top',
            },
          ],
          /* closeOnEscape */ false,
          /* trapFocus */ false
        );
        this.syncMapOverlaySize();
      } else if (!mapOverlayObj && !!this.mapRefId) {
        this.detachOverlayImmediate(this.mapRefId, undefined, false);
        this.mapRefId = null;
        // Re-open the init address-picker map when results overlay closes
        if (this.initMapOverlayConfig) {
          setTimeout(() => {
            if (this.mapContainer) {
              this.routePlanningService._mapOverlaySub.next({
                mapComponent: this.initMapOverlayConfig.mapComponent,
                data: this.initMapOverlayConfig.data,
              });
            }
          }, 0);
        }
      }
    });

    this.observeMapContainerResizes();
    this.showSiteIntroIfNeeded();
  }

  private showSiteIntroIfNeeded(): void {
    const dismissed = this.localStorageService.getItem<boolean>('siteIntroDismissed');
    const isShowIntro = environment.isShowIntro;
    if (dismissed || !isShowIntro) {
      return;
    }
    const videoUrl = environment.videoUrl;
    const moreInfoUrl = environment.moreInfoUrl;
    this.modalService.openSiteIntroModal(videoUrl, moreInfoUrl);
  }

  /**
   * Push the current `mapContainer` bounding-box into the attached overlay so
   * Google Maps gets a correctly-sized parent. Called both right after the
   * overlay is created and whenever the host element resizes (see
   * `observeMapContainerResizes`).
   */
  private syncMapOverlaySize(): void {
    if (!this.mapContainer || !this.mapRefId) {
      return;
    }
    const rect = this.mapContainer.nativeElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      // Skip 0×0 transient states (e.g. drawer mid-animation) — they'd collapse the overlay.
      return;
    }
    const ref = this.overlayEntries[this.mapRefId]?.overlayRef;
    if (!ref) {
      return;
    }
    ref.updateSize({
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    /*
      `updateSize` only writes the new px values onto the pane; CDK does not
      re-run the position strategy on a size change. Without an explicit
      `updatePosition()` the pane keeps its original left/top inline styles
      (calculated for the pre-resize width), and on viewport / drawer-width
      changes the map overlay drifts away from the mapContainer rect. Same
      reasoning as `applyContainerSize` in OverlayComponent — see the
      docstring there.
    */
    ref.updatePosition();
  }

  /**
   * Audit: "verify proper responsiveness for the map". Subscribe to size
   * changes of the map host (drawer collapse/expand on resize, orientation
   * change, devtools open) and re-sync the overlay's width/height. Without
   * this the overlay keeps the dimensions it had at attachment time and the
   * map appears clipped or letter-boxed.
   */
  private observeMapContainerResizes(): void {
    if (this.mapResizeObserver || typeof ResizeObserver === 'undefined') {
      return;
    }
    if (!this.mapContainer?.nativeElement) {
      return;
    }
    this.mapResizeObserver = new ResizeObserver(() => {
      this.syncMapOverlaySize();
    });
    this.mapResizeObserver.observe(this.mapContainer.nativeElement);
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.initMapOverlayConfig.data.title = SEARCH_ADDRESS[language.language];
      this.direction = language.direction;
      this.plannerSurveyOrchestrator.onLanguageChanged();
      if (this.isDesktop) {
        this.reinitializeMapOverlay();
      }
    });
  }

  /**
   * Destroys the current map overlay and re-creates it so the CDK position
   * strategy picks up the new LTR/RTL direction.
   */
  private reinitializeMapOverlay(): void {
    if (this.mapRefId) {
      this.detachOverlayImmediate(this.mapRefId, undefined, false);
      this.mapRefId = null;
    }

    if (this.initMapOverlayConfig && this.mapContainer) {
      setTimeout(() => {
        this.routePlanningService._mapOverlaySub.next({
          mapComponent: this.initMapOverlayConfig.mapComponent,
          data: this.initMapOverlayConfig.data,
        });
      });
    }
  }
}
